// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';

import Sortable from '../Sortable';
import Checkbox from '../Checkbox';
import ConfirmCaution from '../Modals/ConfirmCaution';

import api from '../../api';
import formatDate from '../../utilities/formatDate';
import { BOX_CLIENT_ID, DROPBOX_CLIENT_ID, MS_GRAPH_CLIENT_ID, PKCE_CODE_CHALLENGE, SHAREFILE_ID, GDRIVE_CLIENT_ID, GDRIVE_SCOPE } from '../../data/keys';
import tpa_reports from '../../data/tpa_reports';
import Modal from '../Modals/Modal';
import * as planSync from '../../utilities/planSync';

import '../../styles/sortable.css';
import '../../styles/planeditor.css'; // .cx-status-active / .cx-status-error (Task 1)

// Explainer shown when clicking the Stax CX Plans add-tile. Pairing is
// initiated from CX, so there is no credential form on the TA side.
class CxPlansInfoModal extends Modal {
    renderContent = () => (<div className="modal-content modal-center">
        <div style={{ height: 160 }} />
        <h1>Stax CX Plans</h1>
        <p className="modal-caution-message">
            Pairing starts in <strong>Stax CX</strong>. An admin on your CX team
            connects Trust Accounting from CX&apos;s <strong>Settings &rarr; Integrations
            &rarr; Stax&middot;ai</strong> and chooses &quot;Enable plan sync&quot;.
            Once linked, this page shows the connection status and your Plan Editor
            gains a CX Review inbox.
        </p>
        <div className="modal-caution-buttons">
            <div
                className="modal-caution-decline"
                onClick={() => this.props.modal("")}
            >&larr; Got it, take me back.</div>
        </div>
    </div>);
}

export default class Sync extends React.Component {

    state = {
        showConfirmModal: false,

        // CX plan sync (read from /tpa/plansync/status, never props.team.sync)
        cxSync: null,           // status response or null while loading / on failure
        showCxInfo: false,      // explainer modal
        showCxDisconnect: false, // ConfirmCaution for unlink

        sources: {
            "Storage Services": [
                {
                    id: "dropbox",
                    name: "Dropbox",
                    image: "dropbox.png",
                    modal: "import-dropbox",
                    authUrl: "https://www.dropbox.com/oauth2/authorize?client_id=" + DROPBOX_CLIENT_ID + "&response_type=code&token_access_type=offline&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/dropbox"),
                    params: [ "code" ]
                },
                {
                    id: "onedrive",
                    name: "OneDrive",
                    image: "onedrive.png",
                    modal: "import-onedrive",
                    authUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" + MS_GRAPH_CLIENT_ID + "&response_type=code&response_mode=query&scope=offline_access%20files.read%20files.readwrite%20files.readwrite.all%20sites.read.all&code_challenge_method=S256&code_challenge=" + PKCE_CODE_CHALLENGE + "&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/onedrive"),
                    params: [ "code" ]
                },
                {
                    id: "sharepoint",
                    name: "SharePoint",
                    image: "sharepoint.png",
                    modal: "import-sharepoint",
                    authUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" + MS_GRAPH_CLIENT_ID + "&response_type=code&response_mode=query&scope=offline_access%20files.read%20files.readwrite%20files.readwrite.all%20sites.read.all&code_challenge_method=S256&code_challenge=" + PKCE_CODE_CHALLENGE + "&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/sharepoint"),
                    params: [ "code" ]
                },
                {
                    id: "box",
                    name: "Box",
                    image: "box.png",
                    modal: "import-box",
                    authUrl: `https://account.box.com/api/oauth2/authorize?response_type=code&client_id=`+ BOX_CLIENT_ID + "&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/box"),
                    params: [ "code" ]
                },
                {
                    id: "smartvault",
                    name: "SmartVault",
                    image: "smartvault.png",
                    modal: "import-smartvault",
                },
                {
                    id: "sharefile",
                    name: "ShareFile",
                    image: "sharefile.png",
                    modal: "import-sharefile",
                    authUrl: `https://secure.sharefile.com/oauth/authorize?response_type=code&client_id=`+ SHAREFILE_ID + "&redirect_uri=" + encodeURIComponent("https://my.stax.ai/settings/import/sharefile"),
                    params: [ "code", "subdomain", "apicp" ]
                },
                {
                    id: "egnyte",
                    name: "Egnyte",
                    image: "egnyte.png",
                    modal: "import-egnyte",
                },
                {
                    id:'desktop',
                    name:'Your Computer',
                    image: 'desktop.png',
                    modal: 'import-desktop',
                    wide: false, 
                },
                {
                    id: "googledrive",
                    name: "Google Drive",
                    image: "googledrive.png",
                    modal: "import-googledrive",
                    authUrl: `https://accounts.google.com/o/oauth2/v2/auth?client_id=`+GDRIVE_CLIENT_ID+ "&redirect_uri="+encodeURIComponent("https://my.stax.ai/settings/import/googledrive")+"&response_type=code&scope="+encodeURIComponent(GDRIVE_SCOPE)+"&access_type=offline&prompt=consent",
                    params: [ "code" ]
                }
            ],
            // "Record Keepers": [
            //     {
            //         id: "johnhancock",
            //         name: "John Hancock",
            //         image: "johnhancock.png",
            //         modal: "import-johnhancock",
            //         wide: true
            //     },
            //     {
            //         id: "empower",
            //         name: "Empower",
            //         image: "empower.png",
            //         modal: "import-empower",
            //         wide: true
            //     },
            //     {
            //         id: "voya",
            //         name: "Voya Financial",
            //         image: "voya.png",
            //         modal: "import-voya",
            //         wide: true
            //     },
            //     {
            //         id: "americanfundspp",
            //         name: "Plan Premier",
            //         image: "americanfundspp.png",
            //         modal: "import-americanfundspp",
            //         wide: true
            //     },
            //     {
            //         id: "americanfundsrk",
            //         name: "American Funds",
            //         image: "americanfundsrk.png",
            //         modal: "import-americanfundsrk",
            //         wide: true
            //     }, 
            //     {
            //         id: "ascensus",
            //         name: "Ascensus",
            //         image: "ascensus.png",
            //         modal: "import-ascensus",
            //         wide: true,
            //     },
            // ],
            // "Others": [
            //     {
            //         id: "pensionpro",
            //         name: "PensionPro",
            //         image: "pensionpro.png",
            //         modal: "import-pensionpro"    
            //     },
            // ]
            
        }
    }

   

    componentDidMount = () => {
        const params = new URLSearchParams(window.location.search);
        let syncSource = params.get('source');
        const redirect = params.get('redirect'); 
        if(syncSource && redirect){
            // Find the source and redirect to the appropriate modal 
            syncSource = Object.values(this.state.sources).flat().find(s => s.id === syncSource); 
            // Does the source have an authUrl? Add the state and redirect. 
            if(syncSource && syncSource.authUrl)
                return window.location.href = syncSource.authUrl + "&state=" + encodeURIComponent(JSON.stringify({redirect: redirect})); 
            
            this.props.modal(syncSource.modal); 
        }

        const updateSources = async () => {
            let res;
            try {
                res = await api.get('/sync/list', {}, false, {}, true); 
                if(!res || !res.integrations)
                    res = {integrations: []}; 
            }
            catch (e) {
                res = {integrations: []};   
            }

            const sources = {...this.state.sources};

            // For any sources that are not in the list, set them to enabled... 
            Object.values(sources).flat().forEach(s => {
                const found = res.integrations.find(i => i.name === s.id);
                s.enabled = found? found.enabled: true; 
            }); 

            this.setState({ sources });
        };
        updateSources();

        // CX plan sync status
        const loadCxStatus = async () => {
            const res = await planSync.getStatus();
            this.setState({ cxSync: res && res.success ? res : null });
        };
        loadCxStatus();
        this.loadCxStatus = loadCxStatus; // reused after disconnect

        const query = new URLSearchParams(this.props.location.search);
        const sourceId = this.props.match.params.source
        if (!sourceId) return;

        const source = Object.values(this.state.sources).flat().find(s => s.id === sourceId);
        if (!source) return;

        const error = query.get('error_description');
        if (error)
            return toast.error(error);

        // Load appropriate modal and pass in data
        const data = {};
        if (source.params)
            for (const param of source.params)
                data[param] = query.get(param);

        // Edit an existing connection?
        const editIdx = query.get('edit');
        if (editIdx)
            data.connectionIdx = parseInt(editIdx);

        this.props.modal(source.modal, data);
    }

    updateConnection = (source, idx, diff) => {
        const sync = { ...this.props.team.sync };
        const connections = [ ...sync[source] ];
        connections[idx] = {
            ...connections[idx],
            ...diff
        };
        sync[source] = connections;
        this.props.updateTeam({ sync });
    }

    render = () => {
        let connections = [];
        if (this.props.team && this.props.team.sync) {
            const s = this.props.team.sync;
            for (const source of Object.values(this.state.sources).flat())
                if (s[source.id] && s[source.id].length && s[source.id][0] && s[source.id][0])
                    connections.push(...s[source.id].map((c,idx) => ({
                        ...source,
                        ...c,
                        idx,
                        source: source.id
                    })));
        }

        return (<div className="settings-content" style={{ minWidth: 1000 }}>
            
            <p>
                Stax·ai has bots to help you import your data from various other Cloud data storage applications such as Google Drive, or Dropbox. You can import all your data once, and set up a continuous sync if your business still uploads documents directly to those applications.
            </p>

            {/* ---- Record Sync (CX plan sync) — above file-storage sources ---- */}
            <h2>Record Sync</h2>
            {this.state.cxSync?.connected ? (
                <div
                    className="sortable-list-item import-connections-item"
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/import/cx-plans.png')"
                    }}
                >
                    <div className="sortable-list-name import-connections-source">
                        Stax CX Plans
                        <div className="import-connections-folders">
                            {this.state.cxSync.cxTeamName ? <div className="import-connections-folder-name">
                                Linked to {this.state.cxSync.cxTeamName} (CX)
                            </div> : ""}
                            {this.state.cxSync.lastSyncAt ? <div className="import-connections-folder-name">
                                Last sync {planSync.relativeTime(this.state.cxSync.lastSyncAt)}
                            </div> : ""}
                        </div>
                    </div>

                    <div className="stack-list-meta">
                        {this.state.cxSync.status === "error" ? <div
                            className="import-connections-action cx-status-error"
                            style={{ cursor: 'default' }}
                            title={this.state.cxSync.lastError || "Sync error"}
                        >Error{this.state.cxSync.lastError ? ": " + this.state.cxSync.lastError : ""}</div>
                        : <div
                            className="import-connections-action cx-status-active"
                            style={{ cursor: 'default', textDecoration: 'none' }}
                        >Active</div>}

                        <div
                            className="import-connections-action"
                            onClick={() => window.location.href = "/settings/plan-editor?cxreview=1"}
                        >Review Queue ({this.state.cxSync.pendingCount ?? 0})</div>

                        <div
                            className="import-connections-action"
                            style={{ color: '#FF5D00' }}
                            onClick={() => this.setState({ showCxDisconnect: true })}
                        >Disconnect</div>
                    </div>
                </div>
            ) : (
                <div className="settings-import-source-list">
                    <div
                        className="settings-import-source"
                        title="Stax CX Plans"
                        style={{
                            backgroundImage: "url('https://static.stax.ai/images/import/cx-plans.png')"
                        }}
                        onClick={() => this.setState({ showCxInfo: true })}
                    >Stax CX Plans</div>
                    <div style={{ clear: 'both' }} />
                </div>
            )}

            {connections.length ? <>
                <h2>Existing connections</h2>
                <Sortable
                    title=""
                    data={connections}

                    // Default sort
                    defaultSortBy="source"
                    defaultSortDir={1}

                    // Header configuration
                    header={{
                        name: {
                            prop: "source",
                            label: "Source Application"
                        },
                        meta: [
                            {
                                prop: "user",
                                label: "Created by",
                                width: 220
                            },
                            {
                                prop: "lastChecked",
                                label: "Last Synced",
                                width: 155
                            },
                            {
                                prop: "enabled",
                                label: "Enable",
                                width: 60
                            },
                            {
                                prop: "",
                                label: "",
                                width: 190
                            }
                        ]
                    }}

                    // Line item renderer
                    renderItem={(c, idx, renderMeta) => {

                        const userIdx = this.props.users.findIndex(u => u._id === c.user);
                        const user = userIdx >= 0 ? this.props.users[userIdx] : null;
                        const myIdx = this.props.users.findIndex(u => u._id === (this.props.user ? this.props.user._id : 0));
                        const flatSources = Object.values(this.state.sources).flat(); 

                        const source = flatSources.find(s => s.id === c.source);
                        if (!source) return <React.Fragment key={idx}></React.Fragment>;
        
                        const syncInBackground = Array.isArray(c.folders);
                        const numFolders = (syncInBackground ? c.folders?.length : Object.keys(c.folders ?? {})?.length) ?? 0;

                        return <div
                            className="sortable-list-item import-connections-item"
                            key={idx}
                            style={{
                                backgroundImage: "url('https://static.stax.ai/images/import/" + c.source + ".png')"
                            }}
                            onClick={() => {}}
                        >
                            <div className="sortable-list-name import-connections-source">
                                {c.source === "sharepoint" ? <>
                                    {c.site?.name ?? source.name}
                                    <div className="import-sharepoint-url">{c.siteUrl}</div>
                                </> : <>
                                    {c.title? c.title: source.name}
                                    {c.source === "pensionpro" ? " (" + c.userName + ")" : ""}
                                </>}

                                {/* List of folders */}
                                {c.folders?.length ? <div className="import-connections-folders">
                                    {c.folders.slice(0,3).map((f,jdx) => <div
                                        key={jdx}
                                        className="import-connections-folder-name"
                                    >{f.name ?? tpa_reports[f]}</div>)}
                                    {numFolders > 3 ? <div className="import-connections-folders-more">
                                        and {numFolders - 1} more...
                                    </div> : ""}
                                </div> : ""}
                            </div>
        
                            <div className="stack-list-meta">
                                {renderMeta(user ? <>
                                    <div
                                        className={"sortable-user-icon palette-color-" + String((userIdx-myIdx)%8)}
                                        title={user.name}
                                    >{user.name.substring(0,1)}</div>
                                    {user.name}
                                </> : "Unknown",
                                220, "sortable-list-header-hide-small")}
                                {renderMeta(c.lastChecked ? formatDate(c.lastChecked) : "-", 140)}
                                {renderMeta(syncInBackground ? <Checkbox
                                    className="settings-checkbox import-connections-checkbox"
                                    label=""
                                    checked={!("enabled" in c) || c.enabled}
                                    onToggle={() => "enabled" in c ? this.updateConnection(c.source, c.idx, {
                                        enabled: !c.enabled
                                    }) : ""}
                                /> : "", 75)}
                                {renderMeta(<div className="import-connections-actions">
                                    <div
                                        className="import-connections-action"
                                        style={{
                                            marginLeft: 10,
                                            backgroundImage: "url('https://static.stax.ai/images/icons/action-configure.png')",
                                            cursor: c.enabled? 'pointer': 'not-allowed'
                                        }}
                                        title="Edit connection configuration"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if(!c.enabled)
                                                return; 
                                            window.location.href = "/settings/import/" + c.source + "?edit=" + c.idx
                                        }}
                                    >Edit</div>

                                    <div
                                        className="import-connections-action"
                                        title="Permanently remove connection"
                                        style={{
                                            backgroundImage: "url('https://static.stax.ai/images/icons/delete.png')"
                                        }}
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            this.setState({
                                                showConfirmModal: true,
                                                modalData: {
                                                    message: <>
                                                        If you disconnect this sync account, <a href="https://stax.ai" target="_blank" rel="noopener noreferrer">Stax·ai</a> will stop importing files from this source. The selected folder mappings with also be deleted permanently.
                                                    </>,
                                                    action: async () => {
                                                        const sync = { ...this.props.team.sync };
                                                        const connections = sync[c.source];
                                                        connections.splice(c.idx, 1);
                                                        sync[c.source] = connections;
                                                        await this.props.updateTeam({ sync });
                                                    }
                                                }
                                            })
                                        }}
                                    >Remove</div>
                                </div>, 194)}
                            </div>
                        </div>;
                    }}

                />
                <div style={{ clear: 'both', height: 20 }} />
            </> : ""}

            <h2>Add a new connection</h2>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column'
                }}            
            >
                {
                    Object.entries(this.state.sources).map(([category, options]) => {
                        return (
                            <>
                                <h2
                                    style={{
                                        color: 'var(--color-text-light)'
                                    }}
                                >{category}</h2>
                                <div className="settings-import-source-list">
                                    {options.map((s,idx) => <div
                                        key={idx}
                                        className={`settings-import-source${s.wide? ' settings-import-source-wide': ''}${!s.enabled? ' settings-import-source-disabled': ''}`}
                                        // className={"settings-import-source" + (s.wide ? " settings-import-source-wide" : "")}
                                        title={s.name}
                                        style={{
                                            backgroundImage: "url('https://static.stax.ai/images/import/" + s.image + "')"
                                        }}
                                        onClick={() => {
                                            if(!s.enabled)
                                                return; 
                                            // Do we have any existing connections? If so, prompt to confirm we want to create a new one
                                            const existing = connections.find(c => c.source === s.id);
                                            if(existing){
                                                return this.props.modal("confirm-caution", {
                                                    message: <>
                                                        It looks like you already have a connection for {s.name}, are you sure you want to create another one?
                                                    </>,
                                                    action: () => {
                                                        this.props.modal(s.modal); 
                                                    },
                                                    leaveOpen: true
                                                })
                                            }
                                            if (s.authUrl)
                                                window.location.href = s.authUrl;
                                            else
                                                this.props.modal(s.modal);
                                        }}
                                    >{s.name}</div>)}
                                </div>
                            </>
                        )
                    })
                }
            </div>

            <div style={{ clear: 'both' }} />
            {this.state.showConfirmModal ? <ConfirmCaution
                modal={() => this.setState({ showConfirmModal: false })}
                data={this.state.modalData}
            /> : ""}

            {this.state.showCxInfo ? <CxPlansInfoModal
                modal={() => this.setState({ showCxInfo: false })}
            /> : ""}

            {this.state.showCxDisconnect ? <ConfirmCaution
                modal={() => this.setState({ showCxDisconnect: false })}
                data={{
                    message: <>
                        Disconnecting will stop syncing retirement plans between
                        Trust Accounting and Stax CX. Existing plans and their
                        links are kept, and re-linking from CX resumes them.
                    </>,
                    action: async () => {
                        const res = await planSync.unlink();
                        if (!res?.success) return; // api.js already toasted the error
                        toast.success("Disconnected from Stax CX Plans.");
                        await this.loadCxStatus();
                    }
                }}
            /> : ""}

        </div>);
    }

};